import React, { useCallback, useEffect, useState, useRef } from 'react';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const GamerDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(8, 9, 16, 0.85)',
    backdropFilter: 'blur(12px)',
  },
  '& .MuiPaper-root': {
    background: 'rgba(18, 20, 32, 0.92)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderTop: '2px solid #ff2d9b',
    borderBottom: '2px solid #00d4ff',
    borderRadius: '24px',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 35px rgba(255, 45, 155, 0.25)',
    color: '#ffffff',
    maxWidth: '380px',
    width: '90%',
    margin: '16px',
    overflow: 'hidden',
  },
}));

const VOTE_LABELS = [
  'Toca una estrella para calificar',
  '¡Se esforzó! 😅',
  '¡Buen intento! 👏',
  '¡Muy buena voz! 🎤',
  '¡Gran interpretación! 🔥',
  '¡Estrella de Rock total! 🌟⚡',
];

export default function VotationModal({
  currentVideoData,
  playlist = [],
  assignedPerformer = '',
  setShowVotation,
  stars,
  setStars,
  durationSec,
}) {
  const initialDuration = Number(durationSec) || Number(import.meta.env?.VITE_VOTATION_DURATION_SEC) || 10;
  const [countdown, setCountdown] = useState(initialDuration);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);

  const rawPerformer = (
    assignedPerformer
    || currentVideoData?.assignedTo
    || currentVideoData?.performer
    || (Array.isArray(playlist) ? (playlist.find(it => (it.videoId === currentVideoData?.videoId || it.id === currentVideoData?.videoId))?.assignedTo || '') : '')
    || ''
  ).trim();

  const isGeneric = !rawPerformer || rawPerformer.toLowerCase() === 'cantante';
  const performerName = isGeneric ? 'el cantante' : rawPerformer;

  const triggerHaptic = (duration = 40) => {
    if (typeof window !== 'undefined' && 'navigator' in window && typeof window.navigator.vibrate === 'function') {
      try {
        window.navigator.vibrate(duration);
      } catch (e) {}
    }
  };

  const handleSelectStar = (value) => {
    triggerHaptic(50);
    setStars(value);
  };

  const handleConfirmVote = useCallback(() => {
    triggerHaptic([30, 50, 30]);
    setSubmitted(true);
    if (timerRef.current) clearInterval(timerRef.current);
    setTimeout(() => {
      setShowVotation(false);
    }, 1200);
  }, [setShowVotation]);

  useEffect(() => {
    let timeLeft = initialDuration;
    setCountdown(timeLeft);
    timerRef.current = setInterval(() => {
      timeLeft -= 1;
      setCountdown(timeLeft);
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        setShowVotation(false);
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [setShowVotation, initialDuration]);

  // Color dinámico de la barra según urgencia proporcional al tiempo configurado
  const progressPercent = Math.max(0, Math.min(100, (countdown / initialDuration) * 100));
  const barColor = countdown > initialDuration * 0.5 ? '#00d4ff' : countdown > initialDuration * 0.25 ? '#ffb703' : '#ff2d9b';

  return (
    <GamerDialog open={true}>
      <DialogContent sx={{ padding: '24px 20px', textAlign: 'center', position: 'relative' }}>
        {/* Barra superior de cuenta regresiva */}
        <Box sx={{ width: '100%', marginBottom: '18px' }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: '5px',
              borderRadius: '3px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                boxShadow: `0 0 10px ${barColor}`,
                transition: 'all 0.9s linear',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '6px',
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.72rem' }}>
              Votación en vivo
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: barColor,
                fontSize: '0.78rem',
                letterSpacing: '0.5px',
              }}
            >
              ⏱️ {countdown}s
            </Typography>
          </Box>
        </Box>

        {submitted ? (
          /* Estado de confirmación post-voto */
          <Box
            sx={{
              paddingY: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              animation: 'popIn 0.3s ease-out',
              '@keyframes popIn': {
                '0%': { transform: 'scale(0.8)', opacity: 0 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: '56px', color: '#00d4ff', filter: 'drop-shadow(0 0 16px #00d4ff)' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff' }}>
              ¡Voto Registrado!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
              Tu puntuación de {stars} estrella{stars > 1 ? 's' : ''} fue enviada al anfitrión.
            </Typography>
          </Box>
        ) : (
          /* Formulario interactivo de estrellas */
          <>
            {/* Avatar y Nombre del Cantante */}
            <Box
              sx={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: isGeneric
                  ? 'linear-gradient(135deg, #ff2d9b, #7b2cbf)'
                  : 'linear-gradient(135deg, #00d4ff 0%, #7b2cbf 50%, #ff2d9b 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto',
                fontSize: isGeneric ? '24px' : '22px',
                fontWeight: 900,
                color: '#ffffff',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 0 22px rgba(255, 45, 155, 0.55)',
              }}
            >
              {isGeneric ? '🎤' : performerName.charAt(0).toUpperCase()}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.2rem',
                lineHeight: 1.3,
                marginBottom: '4px',
              }}
            >
              ¿Cómo estuvo el show de{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #ff2d9b, #00d4ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {performerName}
              </span>
              ?
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.55)',
                display: 'block',
                marginBottom: '16px',
                fontSize: '0.78rem',
              }}
            >
              Califica del 1 al 5 antes de que termine el contador
            </Typography>

            {/* Estrellas Gamer con Micro-animación y Glow */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '14px',
              }}
            >
              {[1, 2, 3, 4, 5].map((starVal) => {
                const isSelected = stars >= starVal;
                return (
                  <IconButton
                    key={starVal}
                    onClick={() => handleSelectStar(starVal)}
                    sx={{
                      padding: '8px',
                      transition: 'transform 0.15s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.2)',
                      },
                      '&:active': {
                        transform: 'scale(0.9)',
                      },
                    }}
                  >
                    {isSelected ? (
                      <StarIcon
                        sx={{
                          fontSize: '36px',
                          color: '#ffd166',
                          filter: 'drop-shadow(0 0 10px rgba(255, 209, 102, 0.8))',
                        }}
                      />
                    ) : (
                      <StarBorderIcon
                        sx={{
                          fontSize: '36px',
                          color: 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            color: 'rgba(255, 209, 102, 0.5)',
                          },
                        }}
                      />
                    )}
                  </IconButton>
                );
              })}
            </Box>

            {/* Etiqueta Dinámica de Calificación */}
            <Typography
              variant="body2"
              sx={{
                height: '24px',
                fontWeight: 700,
                color: stars > 0 ? '#00d4ff' : 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.86rem',
                marginBottom: '18px',
                transition: 'color 0.2s ease',
              }}
            >
              {VOTE_LABELS[stars] || VOTE_LABELS[0]}
            </Typography>

            {/* Botón de Confirmación Rápida */}
            <Button
              variant="contained"
              fullWidth
              disabled={stars === 0}
              onClick={handleConfirmVote}
              sx={{
                background: stars > 0
                  ? 'linear-gradient(135deg, #00d4ff 0%, #0077b6 100%)'
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.9rem',
                textTransform: 'none',
                borderRadius: '12px',
                padding: '10px 16px',
                boxShadow: stars > 0 ? '0 4px 18px rgba(0, 212, 255, 0.4)' : 'none',
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #00b4d8 0%, #023e8a 100%)',
                  boxShadow: '0 6px 22px rgba(0, 212, 255, 0.6)',
                },
              }}
            >
              {stars > 0 ? `Enviar Calificación (${stars} ⭐)` : 'Selecciona una estrella'}
            </Button>
          </>
        )}
      </DialogContent>
    </GamerDialog>
  );
}